import { createContext, useMemo, useEffect, useState, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { startOfWeek, format, addMonths } from 'date-fns';
import { useLazyQuery, useMutation } from '@apollo/client';
import { captureException } from '@sentry/react';
import { RECEIVABLES, GET_SHIPPERS, UPDATE_INVOICES } from './graphql';
import { useColumns, QUICKBOOKS_CSV_COLUMNS } from './columns';
import { FILTERS } from './constants';
import { UserContext } from '@/components/App';
import { useWeekStartsOn, useCanViewIntegrations } from '@/hooks';
import { buildPayPeriodWeeks, payPeriodWhere, buildCustomRangeWeek } from '@/utilities/payPeriod';
import { invoiceSearchWhere } from '@/utilities/invoiceSearch';
import {
    buildDelimitedText,
    generateLineItems,
    generateElectroluxRows,
    generateMasterbrandRows,
    isMasterbrandExportEnabled,
    isPelotonExportEnabled,
    downloadPelotonCsv,
} from '../../../Accounting/Invoices/utils';
import { ELECTROLUX_COLUMNS, MASTERBRAND_COLUMNS } from '../../../Accounting/Invoices/columns';
import csvDownload from 'json-to-csv-export';
import * as ROUTES from '@/constants/routes';
import JSZip from 'jszip';
import getOrdersFilesAsPDF from '@/utilities/getOrdersFilesAsPDF';
import { saveAs } from 'file-saver';
import { ASSET_HEAVY } from '@/constants/featureFlags';

export const Context = createContext();

const TODAY = new Date(new Date().setHours(0, 0, 0, 0));
const lastMo = addMonths(TODAY, -1);
const sow = startOfWeek(lastMo, { weekStartsOn: 1 });

const PAGE_SIZE = 50;

export const ContextProvider = ({ children }) => {
    const navigate = useNavigate();
    const { admin_clients, client, teammate, user } = useContext(UserContext);
    const weekStartsOn = useWeekStartsOn();
    const [hasMore, setHasMore] = useState(false);
    const [selectedMap, setSelected] = useState({});
    const [downloadingPODs, setDownloadingPODs] = useState(false);
    const [selectedExportIndex, setSelectedExportIndex] = useState(0);
    const [filter, setFilter] = useState({
        status: FILTERS.NOT_PAID,
        shippers: [],
    });

    const [isAdmin, isOnwardAdmin, isCarrierAdmin] = useMemo(() => {
        const isAdmin = user?.roles?.['ADMIN'] && !client?.client_id && user?.organization_id;
        const isOnwardAdmin = user?.roles?.['ONWARD_ADMIN'] && !client?.client_id;
        const isCarrierAdmin =
            client?.user?.roles?.['CARRIER'] &&
            client?.user?.circles?.[ASSET_HEAVY] &&
            (!teammate?.teammate_id || ['admin', 'invoicing'].some((role) => teammate?.roles?.[role]));
        return [isAdmin, isOnwardAdmin, isCarrierAdmin];
    }, [user, client, teammate]);

    const weeks = useMemo(() => buildPayPeriodWeeks(TODAY, weekStartsOn, 5), [weekStartsOn]);

    // Apply a custom date range from the picker's "Custom Range" option.
    const setCustomRange = useCallback((sow, eow) => {
        const week = buildCustomRangeWeek(sow, eow);
        if (!week) return;
        setFilter((prev) => ({ ...prev, week }));
    }, []);

    const [getInvoices, { data, loading: initInflight, fetchMore }] = useLazyQuery(RECEIVABLES, {
        onError: (err) => {
            console.error(err);
            captureException(err);
        },
    });

    const [getShippers, { data: shipperData }] = useLazyQuery(GET_SHIPPERS, {
        onError: (err) => {
            captureException(err);
        },
    });

    const [updateInvoices, { loading: updateInvoiceInflight }] = useMutation(UPDATE_INVOICES, {
        onError: (err) => {
            console.error(err);
            captureException(err);
        },
    });

    const shippers = useMemo(() => {
        const shippers =
            (shipperData?.results || [])
                .map((invoice) => {
                    return {
                        label: invoice?.client?.business_name,
                        value: invoice.client_id,
                    };
                })
                .sort((l, r) => l.label.localeCompare(r.label)) || [];

        return shippers;
    }, [shipperData]);

    const where = useMemo(() => {
        let where = [];

        if (isOnwardAdmin) {
            where.push({ type: { _eq: 'ONWARD_RECEIVABLE' } });
        } else if (isAdmin) {
            where.push({ type: { _eq: 'RECEIVABLE' } });
            where.push({ organization_id: { _eq: user.organization_id } });
        } else if (isCarrierAdmin) {
            where.push({ type: { _eq: 'RECEIVABLE' } });
            where.push({ partner_client_id: { _eq: client.client_id } });
        } else {
            return {};
        }

        switch (filter.status) {
            case FILTERS.DRAFT:
                where.push({ status: { _eq: 'DRAFT' } });
                break;
            case FILTERS.NOT_PAID:
                where.push({ status: { _eq: 'UNPAID' } });
                break;
            case FILTERS.APPROVED:
                where.push({ status: { _eq: 'APPROVED' } });
                break;
            case FILTERS.PAID:
                where.push({ status: { _eq: 'PAID' } });
                break;
            case FILTERS.REVERTED:
                where.push({ status: { _eq: 'REVERTED' } });
                break;
        }

        if (filter?.shippers?.length) {
            where.push({ client_id: { _in: filter?.shippers } });
        }

        if (filter?.week) {
            where.push(...payPeriodWhere(filter.week));
        }

        if (filter?.searchTerm) {
            where.push(invoiceSearchWhere(filter.searchTerm, ['shipper_orders']));
        }

        return { _and: where };
    }, [filter, user, client, isOnwardAdmin, isAdmin, isCarrierAdmin]);

    useEffect(() => {
        if (Object.keys(where).length > 0) {
            getInvoices({
                variables: {
                    where,
                    limit: PAGE_SIZE,
                    offset: 0,
                },
            }).then(({ data }) => {
                setHasMore(data?.results?.length === PAGE_SIZE);
            });
        }
    }, [where, PAGE_SIZE]);

    useEffect(() => {
        let shipperWhere = [];

        if (isOnwardAdmin) {
            shipperWhere.push({ type: { _eq: 'ONWARD_RECEIVABLE' } });
        } else if (isAdmin) {
            shipperWhere.push({ type: { _eq: 'RECEIVABLE' } });
            shipperWhere.push({ organization_id: { _eq: user.organization_id } });
        } else if (isCarrierAdmin) {
            shipperWhere.push({ type: { _eq: 'RECEIVABLE' } });
            shipperWhere.push({ partner_client_id: { _eq: client.client_id } });
        } else {
            return;
        }

        getShippers({
            variables: {
                where: { _and: shipperWhere },
            },
        });
    }, [isOnwardAdmin, isAdmin, admin_clients, client]);

    const invoices = useMemo(() => {
        return data?.results || [];
    }, [data]);

    const selected = useMemo(() => {
        return Object.keys(selectedMap).filter((attr) => selectedMap[attr]);
    }, [selectedMap]);

    const invoicesMap = useMemo(() => {
        return Object.fromEntries(invoices.map((invoice) => [invoice.carrier_invoice_id, invoice]));
    }, [invoices]);

    const selectedObj = useMemo(() => {
        return selected.map((id) => invoicesMap[id]);
    }, [selected, invoicesMap]);

    const exportOptions = useMemo(() => {
        if (!selectedObj.length) {
            return ['Default Export'];
        }
        const hasElectroluxExport = selectedObj.every(
            (invoice) => invoice?.client?.user?.circles?.['electrolux-custom-export']
        );
        if (hasElectroluxExport) {
            return ['Default Export', 'Electrolux CSV', 'Electrolux TXT'];
        }

        // Offered when every selected invoice's client has the flag — a mixed selection hides it
        // rather than exporting partial data. Available on every status tab, same as Electrolux.
        if (selectedObj.every(isMasterbrandExportEnabled)) {
            return ['Default Export', 'MasterBrand CSV'];
        }
        // Peloton's location invoices are exported together as one aggregated file.
        if (selectedObj.every(isPelotonExportEnabled)) {
            return ['Default Export', 'Peloton CSV'];
        }
        return ['Default Export'];
    }, [selectedObj]);

    useEffect(() => {
        if (selectedExportIndex > exportOptions.length - 1) {
            setSelectedExportIndex(0);
        }
    }, [exportOptions, selectedExportIndex]);

    const loadMore = useCallback(() => {
        fetchMore({
            variables: {
                where,
                limit: PAGE_SIZE,
                offset: data?.results?.length || 0,
            },
            updateQuery: (data, { fetchMoreResult }) => {
                const prev = Object.fromEntries(data.results.map((invoice) => [invoice.carrier_invoice_id, true]));
                const clone = [
                    ...data.results,
                    ...fetchMoreResult.results.filter((invoice) => !prev[invoice.carrier_invoice_id]),
                ];
                return {
                    results: clone,
                };
            },
        }).then((result) => {
            if (result.data.results.length < PAGE_SIZE) {
                setHasMore(false);
            }
        });
    }, [where, fetchMore, PAGE_SIZE, data]);

    const callbacks = {
        exportCsv: () => {
            const exportData = generateLineItems(selectedObj);
            csvDownload({
                headers: QUICKBOOKS_CSV_COLUMNS.map((col) => col.header),
                filename: `receivables-${format(new Date(), 'yyyy-MM-dd')}.csv`,
                delimiter: ',',
                data: exportData.map((row) =>
                    QUICKBOOKS_CSV_COLUMNS.map((col) => {
                        const value = col.getValue(row);
                        return col.format ? col.format(value) : value;
                    })
                ),
            });
        },
        exportElectroluxCsv: () => {
            const exportData = generateElectroluxRows(selectedObj);
            csvDownload({
                headers: ELECTROLUX_COLUMNS.map((col) => col.header),
                filename: `receivables-electrolux-${format(new Date(), 'yyyy-MM-dd')}.csv`,
                delimiter: ',',
                data: exportData.map((row) =>
                    ELECTROLUX_COLUMNS.map((col) => {
                        const value = col.getValue(row);
                        return col.format ? col.format(value) : value;
                    })
                ),
            });
        },
        // One row per order, unlike the line-item-level default and Electrolux exports.
        exportMasterbrandCsv: () => {
            const exportData = generateMasterbrandRows(selectedObj);
            csvDownload({
                headers: MASTERBRAND_COLUMNS.map((col) => col.header),
                filename: `receivables-masterbrand-${format(new Date(), 'yyyy-MM-dd')}.csv`,
                delimiter: ',',
                data: exportData.map((row) =>
                    MASTERBRAND_COLUMNS.map((col) => {
                        const value = col.getValue(row);
                        return col.format ? col.format(value) : value;
                    })
                ),
            });
        },
        exportPelotonCsv: () => downloadPelotonCsv(selectedObj, 'receivables'),
        exportElectroluxTxt: () => {
            const exportData = generateElectroluxRows(selectedObj);
            const txtContent = buildDelimitedText(exportData, ELECTROLUX_COLUMNS, {
                delimiter: '\t',
                lineEnding: '\n',
            });
            const blob = new Blob([txtContent], { type: 'text/plain;charset=utf-8' });
            saveAs(blob, `receivables-electrolux-${format(new Date(), 'yyyy-MM-dd')}.txt`);
        },
        setFilter,
        setCustomRange,
        loadMore,
        onRowClick: (invoice) => {
            const path = ROUTES.ACCOUNTING_INVOICE.replace(':invoice_id', invoice.carrier_invoice_id);
            return navigate(path);
        },
        getRowId: (invoice) => {
            return invoice.carrier_invoice_id;
        },
        setSubmitted: () => {
            updateInvoices({
                variables: {
                    ids: selected,
                    patch: {
                        status: 'UNPAID',
                    },
                },
            });
        },
        setApproved: () => {
            updateInvoices({
                variables: {
                    ids: selected,
                    patch: {
                        status: 'APPROVED',
                    },
                },
            });
        },
        setPaid: () => {
            updateInvoices({
                variables: {
                    ids: selected,
                    patch: {
                        status: 'PAID',
                    },
                },
            });
        },
        submitId: (id) => {
            updateInvoices({
                variables: {
                    ids: [id],
                    patch: {
                        status: 'UNPAID',
                    },
                },
            });
        },
        approveId: (id) => {
            updateInvoices({
                variables: {
                    ids: [id],
                    patch: {
                        status: 'APPROVED',
                    },
                },
            });
        },
        payId: (id) => {
            updateInvoices({
                variables: {
                    ids: [id],
                    patch: {
                        status: 'PAID',
                    },
                },
            });
        },
        cancelId: (id) => {
            updateInvoices({
                variables: {
                    ids: [id],
                    patch: {
                        status: 'CANCELLED',
                    },
                },
            });
        },
        flagRowToggle: (invoice) => {
            updateInvoices({
                variables: {
                    ids: [invoice.carrier_invoice_id],
                    patch: {
                        flagged: !invoice.flagged,
                    },
                },
            });
        },
        selectRows: setSelected,
        setSelectedExportIndex,
        downloadAllPODs: async () => {
            if (!selectedObj || selectedObj.length === 0) return;

            // Collect all orders from all selected invoices
            const allOrders = selectedObj.flatMap((invoice) => invoice.shipper_orders || []);

            if (allOrders.length === 0) return;

            // Check if any orders have PODs
            const hasPODs = allOrders.some((order) => {
                const hasPhoto = order.delivery_photo?.length >= 1 || order.pod?.length >= 1;
                const hasSignature = order.customer_signature?.length >= 1;
                return hasPhoto || hasSignature;
            });

            if (!hasPODs) return;

            setDownloadingPODs(true);
            try {
                const zip = new JSZip();
                const corruptFiles = [];
                let hasAnyFiles = false;

                for (const order of allOrders) {
                    if (order.pod) {
                        const pods = await getOrdersFilesAsPDF(order, 'pod', corruptFiles);
                        if (pods) {
                            hasAnyFiles = true;
                            zip.file(`${order.order_number} - POD.pdf`, await pods.save());
                        }
                    }
                }

                if (hasAnyFiles) {
                    const zipBlob = await zip.generateAsync({ type: 'blob' });
                    const invoiceNumbers = selectedObj.map((inv) => inv.invoice_number).join('_');
                    saveAs(zipBlob, `Receivables_PODs_${invoiceNumbers}.zip`);
                }
            } catch (error) {
                captureException(error);
                console.error(error);
            } finally {
                setDownloadingPODs(false);
            }
        },
    };

    const canViewIntegrations = useCanViewIntegrations();
    const COLUMNS = useColumns({ callbacks, filter, isAdmin: true, canViewIntegrations });

    return (
        <Context.Provider
            value={{
                state: {
                    weeks,
                    hasMore,
                    shippers,
                    selected,
                    invoices,
                    filter,
                    columns: COLUMNS,
                    exportOptions,
                    selectedExportIndex,
                },
                loading: {
                    init: initInflight,
                    update: updateInvoiceInflight,
                    downloadingPODs,
                },
                callbacks,
            }}
        >
            {children}
        </Context.Provider>
    );
};
